import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import { examScheduleAPI, examAPI, subjectAPI } from '../../services/api';

const emptyForm = {
  exam_id: '',
  subject_id: '',
  exam_date: '',
  start_time: '',
  end_time: '',
  room_no: '',
};

const formatDate = (value) => (value ? new Date(value).toLocaleDateString('en-IN') : '-');

export default function ExamSchedule() {
  const [schedule, setSchedule] = useState([]);
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchTerm, setSearchTerm] = useState('');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      const [scheduleRes, examRes, subjectRes] = await Promise.all([
        examScheduleAPI.getAll(),
        examAPI.getAll(),
        subjectAPI.getAll(),
      ]);
      setSchedule(Array.isArray(scheduleRes.data) ? scheduleRes.data : []);
      setExams(Array.isArray(examRes.data) ? examRes.data : []);
      setSubjects(Array.isArray(subjectRes.data) ? subjectRes.data : []);
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error loading exam schedule', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const selectedExam = exams.find((exam) => String(exam.id) === String(formData.exam_id));
  const filteredSubjects = selectedExam?.standard
    ? subjects.filter((subject) => String(subject.standard) === String(selectedExam.standard))
    : subjects;

  const stats = useMemo(() => ({
    entries: schedule.length,
    rooms: new Set(schedule.map((item) => item.room_no).filter(Boolean)).size,
    conflicts: schedule.filter((item) => hasConflict(item, schedule)).length,
  }), [schedule]);

  const handleOpenDialog = (item = null) => {
    if (item) {
      setEditingId(item.id);
      setFormData({
        exam_id: item.exam_id || '',
        subject_id: item.subject_id || '',
        exam_date: item.exam_date || '',
        start_time: item.start_time || '',
        end_time: item.end_time || '',
        room_no: item.room_no || '',
      });
    } else {
      setEditingId(null);
      setFormData(emptyForm);
    }
    setOpenDialog(true);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'exam_id' ? { subject_id: '' } : {}),
    }));
  };

  const localConflicts = () => schedule.filter((item) => {
    if (item.id === editingId) return false;
    if (!formData.room_no || !formData.exam_date || !formData.start_time || !formData.end_time) return false;
    if (item.room_no !== formData.room_no || item.exam_date !== formData.exam_date) return false;
    return formData.start_time < item.end_time && formData.end_time > item.start_time;
  });

  const handleSave = async () => {
    if (!formData.exam_id || !formData.subject_id || !formData.exam_date || !formData.start_time || !formData.end_time) {
      showSnackbar('Exam, subject, date, start time, and end time are required', 'error');
      return;
    }

    if (formData.start_time >= formData.end_time) {
      showSnackbar('Start time must be before end time', 'error');
      return;
    }

    const conflicts = localConflicts();
    if (conflicts.length > 0) {
      const names = conflicts.map((item) => item.subject_name || item.exam_name).join(', ');
      if (!window.confirm(`Room conflict detected with ${names}. Continue anyway?`)) {
        return;
      }
    }

    try {
      if (editingId) {
        await examScheduleAPI.update(editingId, formData);
        showSnackbar('Schedule updated successfully');
      } else {
        await examScheduleAPI.create(formData);
        showSnackbar('Schedule created successfully');
      }
      setOpenDialog(false);
      setEditingId(null);
      fetchAllData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error saving schedule', 'error');
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete ${item.subject_name || 'this schedule'} from the timetable?`)) return;

    try {
      await examScheduleAPI.delete(item.id);
      showSnackbar('Schedule deleted successfully');
      fetchAllData();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting schedule', 'error');
    }
  };

  const filteredSchedule = schedule.filter((item) => {
    const text = [
      item.exam_name,
      item.exam_type,
      item.standard,
      item.subject_name,
      item.subject_code,
      item.room_no,
      item.exam_date,
    ].join(' ').toLowerCase();
    return text.includes(searchTerm.toLowerCase());
  });

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 0.5 }}>
        Exam Schedule
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Build subject-wise timetables and detect room clashes.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Scheduled Papers', value: stats.entries },
          { label: 'Rooms Used', value: stats.rooms },
          { label: 'Room Conflicts', value: stats.conflicts },
        ].map((item) => (
          <Grid item xs={12} md={4} key={item.label}>
            <Paper sx={{ p: 2.5 }}>
              <Typography variant="h4" fontWeight="bold" color={item.label === 'Room Conflicts' && item.value ? 'warning.main' : 'primary'}>
                {item.value}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.label}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Toolbar sx={{ px: 0, mb: 2, gap: 2, flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
          Add Schedule
        </Button>
        <TextField
          placeholder="Search exam, subject, class, room..."
          size="small"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: { xs: '100%', md: 360 }, ml: { md: 'auto' } }}
        />
      </Toolbar>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Exam</TableCell>
                <TableCell>Class</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Time</TableCell>
                <TableCell>Room</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSchedule.length > 0 ? (
                filteredSchedule.map((item) => {
                  const conflict = hasConflict(item, schedule);
                  return (
                    <TableRow key={item.id} hover sx={{ bgcolor: conflict ? '#fff8e1' : 'inherit' }}>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{item.exam_name || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.exam_type || '-'}</Typography>
                      </TableCell>
                      <TableCell>{item.standard || '-'}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={700}>{item.subject_name || '-'}</Typography>
                        <Typography variant="caption" color="text.secondary">{item.subject_code || '-'}</Typography>
                      </TableCell>
                      <TableCell>{formatDate(item.exam_date)}</TableCell>
                      <TableCell>{item.start_time} - {item.end_time}</TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <span>{item.room_no || '-'}</span>
                          {conflict && <Chip icon={<WarningIcon />} label="Conflict" color="warning" size="small" />}
                        </Stack>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleOpenDialog(item)} title="Edit">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(item)} title="Delete" color="error">
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    No schedules found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Exam Schedule' : 'Add Exam Schedule'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Exam"
              name="exam_id"
              value={formData.exam_id}
              onChange={handleInputChange}
            >
              {exams.map((exam) => (
                <MenuItem key={exam.id} value={exam.id}>
                  {exam.exam_name} - Class {exam.standard}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              label="Subject"
              name="subject_id"
              value={formData.subject_id}
              onChange={handleInputChange}
            >
              {filteredSubjects.map((subject) => (
                <MenuItem key={subject.id} value={subject.id}>
                  {subject.subject_name} ({subject.subject_code}) - Class {subject.standard}
                </MenuItem>
              ))}
            </TextField>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Exam Date"
                  name="exam_date"
                  type="date"
                  value={formData.exam_date}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Start Time"
                  name="start_time"
                  type="time"
                  value={formData.start_time}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="End Time"
                  name="end_time"
                  type="time"
                  value={formData.end_time}
                  onChange={handleInputChange}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </Grid>
            <TextField
              fullWidth
              label="Room No"
              name="room_no"
              value={formData.room_no}
              onChange={handleInputChange}
            />
            {localConflicts().length > 0 && (
              <Alert severity="warning">
                This room overlaps with another scheduled paper.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function hasConflict(item, allItems) {
  if (!item.room_no || !item.exam_date || !item.start_time || !item.end_time) return false;

  return allItems.some((other) => {
    if (other.id === item.id) return false;
    if (other.room_no !== item.room_no || other.exam_date !== item.exam_date) return false;
    return item.start_time < other.end_time && item.end_time > other.start_time;
  });
}
